import { Component, Input, inject } from "@angular/core";
import { ControlModel } from "@app/controls/models/ControlModel";
import { ControlDataService } from "@app/controls/services/control.data.service";
import { Observable, firstValueFrom } from "rxjs";
import { LinkModel } from "@models/generic/LinkModel";
import { IncidentControlsService } from "@app/incident/services/incident-controls.service";
import { SettingsDataService } from "@services/http/SettingsDataService";
import { ApplicationSettings } from "@services/http/settings/application-settings";
import { IncidentLinkModel } from "@app/incident/models/incident-link.model";
import moment from "moment";
import { TranslocoService } from "@jsverse/transloco";

@Component({
    selector: "control-events",
    templateUrl: "./control-events.component.html",
    styleUrls: ["./control-events.component.scss"],
    standalone: false,
})
export class ControlEventComponent {
    private _controlDS = inject(ControlDataService);
    private incidentControlsDS = inject(IncidentControlsService);
    private settingsDS = inject(SettingsDataService);
    private _translocoService = inject(TranslocoService);

    @Input() controlModel: ControlModel;
    @Input() disabled = true;

    linked: LinkModel[] = [];
    unlinked: LinkModel[] = [];
    linkedIncidents: IncidentLinkModel[];

    isLoading = true;
    isLoadingIncidents = false;
    useIncidents = false;

    hasAccessChecks = {};
    accessCheckInProgress = false;
    displayValueProp: string;

    async load() {
        await firstValueFrom(this._translocoService.load(`controls/${this._translocoService.getActiveLang()}`));

        this.useIncidents = (
            await firstValueFrom(this.settingsDS.getSetting(ApplicationSettings.UseIncidents))
        ).BoolValue;

        if (this.useIncidents) {
            this.isLoadingIncidents = true;
            this.incidentControlsDS
                .getLinkedIncidentsAsync(this.controlModel.Guid)
                .subscribe((data) => {
                    const mappedIncidents: IncidentLinkModel[] = [];
                    if (data) {
                        data.forEach((item) => {
                            item.displayItems = [];

                            item.displayItems.push({
                                Header: this._translocoService.translate("controls.controlEvents.descriptionHeader"),
                                Value: item.description,
                            });

                            item.displayItems.push({
                                Header: this._translocoService.translate("controls.controlEvents.statusHeader"),
                                Value: item.status,
                            });

                            if (item.due_Date !== null && item.due_Date !== undefined) {
                                const due_Date = moment(item.due_Date).format("DD-MM-YYYY");

                                item.displayItems.push({
                                    Header: this._translocoService.translate("controls.controlEvents.dueDateHeader"),
                                    Value: due_Date,
                                });
                            } else {
                                item.displayItems.push({
                                    Header: this._translocoService.translate("controls.controlEvents.dueDateHeader"),
                                    Value: null,
                                });
                            }

                            if (item.date_Occurred !== null && item.date_Occurred !== undefined) {
                                const date_Occurred = moment(item.date_Occurred).format(
                                    "DD-MM-YYYY",
                                );

                                item.displayItems.push({
                                    Header: this._translocoService.translate("controls.controlEvents.dateOccurredHeader"),
                                    Value: date_Occurred,
                                });
                            } else {
                                item.displayItems.push({
                                    Header: this._translocoService.translate("controls.controlEvents.dateOccurredHeader"),
                                    Value: null,
                                });
                            }

                            mappedIncidents.push(item);
                        });
                    }
                    this.linkedIncidents = mappedIncidents;
                    this.isLoadingIncidents = false;
                });
        }

        this.getLinkedEvents(this.controlModel.Guid).subscribe((data) => {
            this.loadData(data);
            this.isLoading = false;
        });
    }

    loadData(data: LinkModel[]): void {
        const linked = [];
        data.forEach((item) => {
            linked.push(item);
        });
        this.linked = linked;
    }

    getLinkedEvents(controlGuid: string): Observable<LinkModel[]> {
        return this._controlDS.getLinkableEvents(controlGuid);
    }
}
